import { useEffect, useState } from "react";
import { loadStripe, SetupIntent, Stripe } from "@stripe/stripe-js";
import tracking from "frontend/tracking";

function handleSetupIntentStatus(status: SetupIntent.Status, errorMessage?: string) {
  switch (status) {
    case "succeeded": {
      tracking.trackAnalyticsEvent("trial_setup_intent_succeeded");
      break;
    }

    case "requires_payment_method": {
      tracking.trackAnalyticsEvent("trial_setup_intent_failed", {
        status,
        error_message: errorMessage,
      });
      break;
    }

    case "requires_confirmation": {
      tracking.trackAnalyticsEvent("trial_setup_intent_failed", {
        status,
        error_message: errorMessage,
      });
      break;
    }

    case "requires_action": {
      tracking.trackAnalyticsEvent("trial_setup_intent_failed", {
        status,
        error_message: errorMessage,
      });
      break;
    }

    case "canceled": {
      tracking.trackAnalyticsEvent("trial_setup_intent_canceled", {
        status,
      });
      break;
    }

    case "processing": {
      tracking.trackAnalyticsEvent("trial_setup_intent_processing", {
        status,
      });
      break;
    }

    default: {
      tracking.trackAnalyticsEvent("trial_setup_intent_unknown_status", {
        status,
      });
    }
  }
}

function removeSetupIntentParamsFromUrl() {
  const url = new URL(window.location.href);
  const paramsToRemove = ["setup_intent", "setup_intent_client_secret", "redirect_status"];

  for (const param of paramsToRemove) {
    url.searchParams.delete(param);
  }

  window.history.replaceState({}, "", url.toString());
}

export function useStripeSetupIntentTracking(
  clientSecret: string | null,
  setupIntentId: string | null,
  redirectStatus: string | null
) {
  const [stripe, setStripe] = useState<Stripe | null>(null);

  // Load Stripe
  useEffect(() => {
    if (process.env.STRIPE_PKEY) {
      loadStripe(process.env.STRIPE_PKEY).then(setStripe).catch(console.error);
    }
  }, []);

  useEffect(() => {
    const handleSetupIntentRedirect = async () => {
      if (!stripe || !redirectStatus || !setupIntentId || !clientSecret) {
        return;
      }

      try {
        // Retrieve the full Setup Intent details
        const { setupIntent, error: stripeError } = await stripe.retrieveSetupIntent(clientSecret);

        if (stripeError) {
          tracking.trackAnalyticsEvent("trial_setup_intent_retrieval_failed", {
            error_message: stripeError.message,
          });
          return;
        }

        handleSetupIntentStatus(setupIntent.status, setupIntent.last_setup_error?.message);

        removeSetupIntentParamsFromUrl();
      } catch (error) {
        tracking.trackAnalyticsEvent("trial_setup_intent_retrieval_failed", {
          error_message: error instanceof Error ? error.message : "Unknown error",
        });
      }
    };

    // Only run if stripe is available
    handleSetupIntentRedirect();
  }, [stripe, clientSecret, setupIntentId, redirectStatus]);
}
